/* eslint-disable react/jsx-no-bind */
import React from 'react';
import classNames from 'classnames';
import {AtomicBlockUtils, CompositeDecorator, Editor, EditorState, RichUtils, Modifier, SelectionState} from 'draft-js';
import {stateToHTML} from 'draft-js-export-html';
import {stateFromHTML} from 'draft-js-import-html';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {Modal, ModalHeader, ModalBody} from 'reactstrap';
import {FormattedMessage} from 'react-intl';
import messages from '../../../res/translations/messages';
import EntityLink from './EntityLink';
import ToolbarButton from './ToolbarButton';
import Media from './Media';
import MediaGalleryModal from '../media/MediaGalleryModal';
import {stripHtml} from '../../utils/text';
import {constants} from '@neosvoc/nv-platform-core-common';

const UNHANDLED_TYPES = new Set([
    constants.EDITOR_TEMPLATES.RANK_1,
    constants.EDITOR_TEMPLATES.GENERATOR_CLASSIFIER,
    constants.EDITOR_TEMPLATES.IMAGE_1,
    constants.EDITOR_TEMPLATES.SINGLE_CHOICE_1_EXT,
    constants.EDITOR_TEMPLATES.GEO_1,
]);

const BRACKET_ROW_TYPES = new Set([
    constants.EDITOR_TEMPLATES.BATTERY_1,
    constants.EDITOR_TEMPLATES.BATTERY_2,
]);

const UNDERSCORE_ROW_TYPES = new Set([
    constants.EDITOR_TEMPLATES.MULTIPLE_NUMBER_1,
    constants.EDITOR_TEMPLATES.MULTIPLE_NUMBER_2,
    constants.EDITOR_TEMPLATES.MULTIPLE_NUMBER_3,
    constants.EDITOR_TEMPLATES.MULTIPLE_NUMBER_4,
]);

const getAnswerTemplate = (q, row) => {
    if (!row) return `{{answers.${q.id}}}`;
    if (BRACKET_ROW_TYPES.has(q.type)) return `{{answers.${q.id}[${row.id}]}}`;
    if (UNDERSCORE_ROW_TYPES.has(q.type)) return `{{answers.${q.id}_${row.id}}}`;
    return `{{answers.${q.id}.${row.id}}}`;
};

const getRowText = (row) => {
    const label = row.label || row.value;
    if (label) return stripHtml(label);
    const parts = [row.minLabel, row.maxLabel].filter(Boolean);
    return parts.join(' - ');
};

const ENTITY_LINK = 'LINK';
const ENTITY_IMAGE = 'IMG';

const getStrategy = (entityType) => (
    (contentBlock, callback, contentState) => {
        contentBlock.findEntityRanges(
            (character) => {
                const entityKey = character.getEntity();
                return entityKey !== null && contentState.getEntity(entityKey).getType() === entityType;
            },
            callback,
        );
    }
);

// draft-js-import-element always treats `<img>` as an inline element (it's hardcoded in its
// INLINE_ELEMENTS table, regardless of customBlockFn/customInlineFn), so a bare `<img>` sitting
// directly inside a text block gets imported as an invisible zero-width entity glued to a
// placeholder character in the surrounding text, with no visual rendering (no CompositeDecorator
// handles ENTITY_IMAGE inline) and no way for the user to reliably select/delete it as a unit.
// Images inserted through the toolbar avoid this because handleImageSelect always builds them as
// a dedicated atomic block, which round-trips through `<figure>` on save/reload and renders as a
// real, deletable-as-one-unit <img> via the Media block renderer. Wrapping any bare `<img>` in a
// `<figure>` before parsing forces the same atomic-block path for images that were saved (e.g. by
// an older version of this editor, or by external HTML) without that wrapper.
const wrapBareImages = (html) => {
    if (!html || html.indexOf('<img') === -1) {
        return html;
    }
    const container = document.createElement('div');
    container.innerHTML = html;
    container.querySelectorAll('img').forEach((img) => {
        if (img.parentNode && img.parentNode.tagName === 'FIGURE') {
            return;
        }
        const figure = document.createElement('figure');
        img.parentNode.insertBefore(figure, img);
        figure.appendChild(img);
    });
    return container.innerHTML;
};

const stateFromHTMLOptions = {
    customInlineFn: (element, {Entity}) => {
        switch (element.tagName.toUpperCase()) {
            case 'A':
                return Entity(ENTITY_LINK, {href: element.getAttribute('href')}); // eslint-disable-line new-cap
            case 'IMG':
                return Entity(ENTITY_IMAGE, { // eslint-disable-line new-cap
                    src: element.getAttribute('src'),
                    assetId: element.getAttribute('data-asset-id') || undefined,
                });
        }
    },
    customBlockFn: (element) => {
        const tagName = element.tagName && element.tagName.toUpperCase();
        if (!['P', 'DIV', 'LI', 'FIGURE'].includes(tagName)) {
            return;
        }
        const styleAlign = element.style && element.style.textAlign;
        const attrAlign = element.getAttribute && element.getAttribute('align');
        const textAlign = styleAlign || attrAlign;
        if (!textAlign) {
            return;
        }
        return {
            data: {
                'text-align': textAlign,
            },
        };
    },
};

const stateToHTMLOptions = {
    entityStyleFn: (entity) => {
        const entityType = entity.get('type');
        if (entityType === ENTITY_IMAGE) {
            const data = entity.getData();
            const attributes = {src: data.src};
            if (data.assetId) {
                attributes['data-asset-id'] = data.assetId;
            }
            return {
                element: 'img',
                attributes,
            };
        }
    },
    blockStyleFn: (block) => {
        const textAlign = block.getData().get('text-align');
        if (!textAlign) {
            return;
        }
        return {
            style: {
                textAlign,
            },
        };
    },
};

const decorator = new CompositeDecorator([
    {
        strategy: getStrategy(ENTITY_LINK),
        component: EntityLink,
    },
]);

const mediaBlockRenderer = (block) => {
    if (block.getType() === 'atomic') {
        return {
            component: Media,
            editable: false,
        };
    }

    return null;
};

// Draft.js does not treat atomic blocks (our images) as a single indivisible unit for
// backspace/delete: by default, pressing delete right before one (or backspace right after
// one) just merges it into the neighboring text block, silently downgrading its type from
// 'atomic' to that of the surviving block. The image disappears from view (the Media block
// renderer only renders 'atomic' blocks), but the entity char carrying data-asset-id survives,
// invisibly embedded in the merged block's text - so the reference is never actually cleared.
// We remove the whole atomic block ourselves by merging it into whichever block precedes it:
// that block contributes all of its own text and none of the atomic block's, so it keeps its
// own type/content untouched and the atomic block simply disappears without a trace.
const removeAtomicBlock = (contentState, atomicKey) => {
    const atomicBlock = contentState.getBlockForKey(atomicKey);
    const prevKey = contentState.getKeyBefore(atomicKey);
    if (prevKey) {
        const prevBlock = contentState.getBlockForKey(prevKey);
        const selection = SelectionState.createEmpty(prevKey).merge({
            anchorKey: prevKey,
            anchorOffset: prevBlock.getLength(),
            focusKey: atomicKey,
            focusOffset: atomicBlock.getLength(),
        });
        return Modifier.removeRange(contentState, selection, 'forward');
    }
    // The atomic block is the very first block in the editor: there is nothing before it to
    // merge into, so just empty it in place (Media already renders nothing for an atomic block
    // without an entity).
    const selection = SelectionState.createEmpty(atomicKey).merge({
        anchorOffset: 0,
        focusOffset: atomicBlock.getLength(),
    });
    return Modifier.removeRange(contentState, selection, 'forward');
};

const findAdjacentAtomicBlockKey = (editorState, command) => {
    const selection = editorState.getSelection();
    const content = editorState.getCurrentContent();

    if (!selection.isCollapsed()) {
        // An atomic block that is explicitly selected (e.g. the user clicked on the image) is
        // represented as a non-collapsed selection spanning that single block.
        const key = selection.getStartKey();
        if (key === selection.getEndKey() && content.getBlockForKey(key).getType() === 'atomic') {
            return key;
        }
        return null;
    }

    const key = selection.getStartKey();
    const offset = selection.getStartOffset();
    const block = content.getBlockForKey(key);

    if (command === 'delete' && offset === block.getLength()) {
        const nextKey = content.getKeyAfter(key);
        if (nextKey && content.getBlockForKey(nextKey).getType() === 'atomic') {
            return nextKey;
        }
    }

    if (command === 'backspace' && offset === 0) {
        const prevKey = content.getKeyBefore(key);
        if (prevKey && content.getBlockForKey(prevKey).getType() === 'atomic') {
            return prevKey;
        }
    }

    return null;
};

const getCurrentBlockAlignment = (editorState) => {
    const selection = editorState.getSelection();
    const contentState = editorState.getCurrentContent();
    const block = contentState.getBlockForKey(selection.getStartKey());
    return block.getData().get('text-align') || 'left';
};

const editorBlockStyleFn = (block) => {
    const textAlign = block.getData().get('text-align');
    if (!textAlign) {
        return '';
    }
    return `rte-align-${textAlign}`;
};

class RichEditor extends React.PureComponent {
    constructor(props) {
        super(props);
        const html = props.value || '';
        this.state = {
            editorState: EditorState.createWithContent(stateFromHTML(wrapBareImages(html), stateFromHTMLOptions), decorator),
            html,
            mediaModalOpen: false,
            expanded: false,
        };
        this.toggleExpanded = this.toggleExpanded.bind(this);
        this.handleChange = this.handleChange.bind(this);
        this.handleKeyCommand = this.handleKeyCommand.bind(this);
        this.handleBold = this.handleInline.bind(this, 'BOLD');
        this.handleItalic = this.handleInline.bind(this, 'ITALIC');
        this.handleUnderline = this.handleInline.bind(this, 'UNDERLINE');
        this.handleAlign = this.handleAlign.bind(this);
        this.handleAlignLeft = this.handleAlign.bind(this, 'left');
        this.handleAlignCenter = this.handleAlign.bind(this, 'center');
        this.handleAlignRight = this.handleAlign.bind(this, 'right');
        this.handleLink = this.handleEntityLink.bind(this, {defaultHref: '', entityType: ENTITY_LINK, promptHref: true});
        this.handleEmailLink = this.handleEntityLink.bind(this, {defaultHref: '{{url}}', entityType: ENTITY_LINK, promptHref: false});
        this.toggleMediaModal = this.toggleMediaModal.bind(this);
        this.handleImageSelect = this.handleImageSelect.bind(this);
        this.toggleParamsDropdown = this.toggleParamsDropdown.bind(this);
        this.handleInsertParameter = this.handleInsertParameter.bind(this);
        this.toggleExtraDropdown = this.toggleExtraDropdown.bind(this);
        this.handleInsertExtraField = this.handleInsertExtraField.bind(this);
        this.toggleQuestionsDropdown = this.toggleQuestionsDropdown.bind(this);
        this.handleInsertQuestion = this.handleInsertQuestion.bind(this);
        this.editorRef = React.createRef();
    }

    componentDidUpdate(prevProps) {
        if (prevProps.value !== this.props.value && this.state.html !== this.props.value) {
            // value changed externally: re-create new editor state
            const html = this.props.value;
            this.setState({
                editorState: EditorState.createWithContent(stateFromHTML(wrapBareImages(html), stateFromHTMLOptions), decorator),
                html,
            });
        }
    }

    handleChange(editorState) {
        const html = stateToHTML(editorState.getCurrentContent(), stateToHTMLOptions);
        this.setState({editorState, html}, () => {
            this.props.onChange(html);
        });
    }

    handleKeyCommand(command) {
        if (command !== 'backspace' && command !== 'delete') {
            return 'not-handled';
        }
        const {editorState} = this.state;
        const atomicKey = findAdjacentAtomicBlockKey(editorState, command);
        if (!atomicKey) {
            return 'not-handled';
        }
        const newContent = removeAtomicBlock(editorState.getCurrentContent(), atomicKey);
        this.handleChange(EditorState.push(editorState, newContent, 'remove-range'));
        return 'handled';
    }

    handleInline(style, e) {
        e.preventDefault();
        this.handleChange(RichUtils.toggleInlineStyle(this.state.editorState, style));
    }

    handleAlign(alignment, e) {
        e.preventDefault();
        const {editorState} = this.state;
        const contentState = editorState.getCurrentContent();
        const selection = editorState.getSelection();
        const block = contentState.getBlockForKey(selection.getStartKey());
        const blockData = block.getData();
        const newBlockData = blockData.set('text-align', alignment);
        const newContentState = Modifier.setBlockData(contentState, selection, newBlockData);
        const newEditorState = EditorState.push(editorState, newContentState, 'change-block-data');
        this.handleChange(newEditorState);
    }

    handleEntityLink({defaultHref, entityType, promptHref}) {
        const {editorState} = this.state;
        const selection = editorState.getSelection();
        if (!selection.isCollapsed()) {
            const contentState = editorState.getCurrentContent();
            const startKey = editorState.getSelection().getStartKey();
            const startOffset = editorState.getSelection().getStartOffset();
            const blockWithLinkAtBeginning = contentState.getBlockForKey(startKey);
            const linkKey = blockWithLinkAtBeginning.getEntityAt(startOffset);

            let href = defaultHref;
            if (linkKey) {
                const linkInstance = contentState.getEntity(linkKey);
                href = linkInstance.getData().href;
            }
            if (promptHref) {
                href = prompt('URL:', href);
            }
            if (!href) {
                return;
            }
            const contentStateWithEntity = contentState.createEntity(
                entityType,
                'MUTABLE',
                {href, target: '_blank'},
            );
            const entityKey = contentStateWithEntity.getLastCreatedEntityKey();
            const newEditorState = EditorState.set(editorState, {currentContent: contentStateWithEntity});
            this.handleChange(
                RichUtils.toggleLink(
                    newEditorState,
                    newEditorState.getSelection(),
                    entityKey,
                ),
            );
        }
    }

    toggleMediaModal() {
        this.setState((s) => ({mediaModalOpen: !s.mediaModalOpen}));
    }

    toggleExpanded() {
        this.setState((s) => ({expanded: !s.expanded}));
    }

    handleImageSelect({assetId, url}) {
        const {editorState} = this.state;
        const contentState = editorState.getCurrentContent();
        const contentStateWithEntity = contentState.createEntity(
            ENTITY_IMAGE,
            'IMMUTABLE',
            {src: url, assetId},
        );
        const entityKey = contentStateWithEntity.getLastCreatedEntityKey();
        const newEditorState = EditorState.set(
            editorState,
            {currentContent: contentStateWithEntity},
        );
        this.handleChange(
            AtomicBlockUtils.insertAtomicBlock(
                newEditorState,
                entityKey,
                ' ',
            ),
        );
    }

    toggleParamsDropdown(e) {
        e && e.preventDefault();
        this.setState((s) => ({paramsDropdownOpen: !s.paramsDropdownOpen}));
    }

    handleInsertParameter(name) {
        if (!name) return;
        const text = `{{parameters.${name}}}`;
        const {editorState} = this.state;
        const contentState = editorState.getCurrentContent();
        const selection = editorState.getSelection();
        const newContent = Modifier.insertText(contentState, selection, text);
        const newEditorState = EditorState.push(editorState, newContent, 'insert-characters');
        this.setState({paramsDropdownOpen: false});
        this.handleChange(newEditorState);
        if (this.editorRef && this.editorRef.current && this.editorRef.current.focus) {
            this.editorRef.current.focus();
        }
    }

    toggleQuestionsDropdown(e) {
        e && e.preventDefault();
        this.setState((s) => ({questionsDropdownOpen: !s.questionsDropdownOpen}));
    }

    handleInsertQuestion(text) {
        if (!text) return;
        const {editorState} = this.state;
        const contentState = editorState.getCurrentContent();
        const selection = editorState.getSelection();
        const newContent = Modifier.insertText(contentState, selection, text);
        const newEditorState = EditorState.push(editorState, newContent, 'insert-characters');
        this.setState({questionsDropdownOpen: false});
        this.handleChange(newEditorState);
        if (this.editorRef && this.editorRef.current && this.editorRef.current.focus) {
            this.editorRef.current.focus();
        }
    }

    toggleExtraDropdown(e) {
        e && e.preventDefault();
        this.setState((s) => ({extraDropdownOpen: !s.extraDropdownOpen}));
    }

    handleInsertExtraField(name) {
        if (!name) return;
        const text = `{{extraFields.${name}}}`;
        const {editorState} = this.state;
        const contentState = editorState.getCurrentContent();
        const selection = editorState.getSelection();
        const newContent = Modifier.insertText(contentState, selection, text);
        const newEditorState = EditorState.push(editorState, newContent, 'insert-characters');
        this.setState({extraDropdownOpen: false});
        this.handleChange(newEditorState);
        if (this.editorRef && this.editorRef.current && this.editorRef.current.focus) {
            this.editorRef.current.focus();
        }
    }

    render() {
        const {
            className, hasEmailLink, hasImage, showParameters, showExtraFields, showQuestions, questions,
            // Formik's <Field> injects these for RichEditorField; RichEditor already reads them itself
            // (this.props.value/.form) or handles change/blur internally, so they must not be spread onto
            // the wrapping div below — otherwise a native "change"/"blur" event bubbling up from anything
            // rendered inside (e.g. a file input inside a portal-rendered modal) would hit that DOM handler
            // and overwrite the Formik field value with a raw SyntheticEvent instead of the RTE html.
            onChange, onBlur, value, form, // eslint-disable-line no-unused-vars
            ...rest
        } = this.props;
        const {editorState, expanded} = this.state;
        const inlineStyle = editorState.getCurrentInlineStyle();
        const isSelectionCollapsed = editorState.getSelection().isCollapsed();
        const currentAlignment = getCurrentBlockAlignment(editorState);
        const allowedQuestions = (questions || []).filter((q) => !UNHANDLED_TYPES.has(q.type));
        const toolbar = (
            <div className="btn-toolbar mb-2">
                <div className="btn-group btn-group-sm">
                    <ToolbarButton onClick={this.handleBold} active={inlineStyle.includes('BOLD')}>
                        <FontAwesomeIcon icon="bold" fixedWidth={true} />
                    </ToolbarButton>
                    <ToolbarButton onClick={this.handleItalic} active={inlineStyle.includes('ITALIC')}>
                        <FontAwesomeIcon icon="italic" fixedWidth={true} />
                    </ToolbarButton>
                    <ToolbarButton onClick={this.handleUnderline} active={inlineStyle.includes('UNDERLINE')}>
                        <FontAwesomeIcon icon="underline" fixedWidth={true} />
                    </ToolbarButton>
                </div>
                <div className="btn-group btn-group-sm ml-1">
                    <ToolbarButton onClick={this.handleAlignLeft} active={currentAlignment === 'left'}>
                        <FontAwesomeIcon icon="align-left" fixedWidth={true} />
                    </ToolbarButton>
                    <ToolbarButton onClick={this.handleAlignCenter} active={currentAlignment === 'center'}>
                        <FontAwesomeIcon icon="align-center" fixedWidth={true} />
                    </ToolbarButton>
                    <ToolbarButton onClick={this.handleAlignRight} active={currentAlignment === 'right'}>
                        <FontAwesomeIcon icon="align-right" fixedWidth={true} />
                    </ToolbarButton>
                </div>
                <div className="btn-group btn-group-sm ml-1">
                    <ToolbarButton onClick={this.handleLink} disabled={isSelectionCollapsed}>
                        <FontAwesomeIcon icon="link" fixedWidth={true} />
                    </ToolbarButton>
                    {hasEmailLink && <ToolbarButton onClick={this.handleEmailLink} disabled={isSelectionCollapsed}>
                        <FontAwesomeIcon icon="external-link-alt" fixedWidth={true} />
                    </ToolbarButton>}
                </div>
                {hasImage && <div className="btn-group btn-group-sm ml-1">
                    <ToolbarButton onClick={this.toggleMediaModal}>
                        <FontAwesomeIcon icon={['far', 'image']} fixedWidth={true} />
                    </ToolbarButton>
                </div>}
                {showParameters && (
                    <div className="btn-group btn-group-sm ml-1">
                        <div className={classNames('dropdown', {show: this.state.paramsDropdownOpen})}>
                            <button type="button" className="btn btn-outline-dark btn-sm dropdown-toggle" onClick={this.toggleParamsDropdown}>
                                <FormattedMessage {...messages.surveys.form.title.paramQs} />
                            </button>
                            <div className={classNames('dropdown-menu', {show: this.state.paramsDropdownOpen})}>
                                {((this.props.form && this.props.form.values.parameters) || []).length === 0 && (
                                    <button className="dropdown-item" type="button" disabled>—</button>
                                )}
                                {((this.props.form && this.props.form.values.parameters) || []).map((p, i) => (
                                    <button key={i} className="dropdown-item" type="button" onClick={() => this.handleInsertParameter(p.name)}>
                                        {p.name}
                                    </button>
                                ))}
                            </div>
                        </div>
                    </div>
                )}
                {showExtraFields && (
                    <div className="btn-group btn-group-sm ml-1">
                        <div className={classNames('dropdown', {show: this.state.extraDropdownOpen})}>
                            <button type="button" className="btn btn-outline-dark btn-sm dropdown-toggle" onClick={this.toggleExtraDropdown}>
                                <FormattedMessage {...messages.surveys.form.title.paramTracker} />
                            </button>
                            <div className={classNames('dropdown-menu', {show: this.state.extraDropdownOpen})}>
                                {((this.props.form && this.props.form.values.extraFields) || []).length === 0 && (
                                    <button className="dropdown-item" type="button" disabled>—</button>
                                )}
                                {((this.props.form && this.props.form.values.extraFields) || []).map((p, i) => (
                                    <button key={i} className="dropdown-item" type="button" onClick={() => this.handleInsertExtraField(p.name)}>
                                        {p.name}
                                    </button>
                                ))}
                            </div>
                        </div>
                    </div>
                )}
                {showQuestions && (
                    <div className="btn-group btn-group-sm ml-1">
                        <div className={classNames('dropdown', {show: this.state.questionsDropdownOpen})}>
                            <button type="button" className="btn btn-outline-dark btn-sm dropdown-toggle" onClick={this.toggleQuestionsDropdown}>
                                <FormattedMessage {...messages.surveys.form.title.questions} />
                            </button>
                            <div className={classNames('dropdown-menu', {show: this.state.questionsDropdownOpen})}>
                                {(!allowedQuestions || allowedQuestions.length === 0) && (
                                    <button className="dropdown-item" type="button" disabled>—</button>
                                )}
                                {(allowedQuestions || []).map((q, i) => {
                                    if (Array.isArray(q.rows) && q.rows.length > 0) {
                                        return (
                                            <React.Fragment key={i}>
                                                {i > 0 && <div className="dropdown-divider" />}
                                                <h6 className="dropdown-header">{q.id} - {stripHtml(q.title || '')}</h6>
                                                {q.rows.map((row, j) => (
                                                    <button key={j} className="dropdown-item pl-4" type="button"
                                                        onClick={() => this.handleInsertQuestion(getAnswerTemplate(q, row))}>
                                                        {row.id} - {getRowText(row)}
                                                    </button>
                                                ))}
                                            </React.Fragment>
                                        );
                                    }
                                    return (
                                        <React.Fragment key={i}>
                                            {i > 0 && <div className="dropdown-divider" />}
                                            <button className="dropdown-item" type="button"
                                                onClick={() => this.handleInsertQuestion(getAnswerTemplate(q, null))}>
                                                {q.id} - {stripHtml(q.title || '')}
                                            </button>
                                        </React.Fragment>
                                    );
                                })}
                            </div>
                        </div>
                    </div>
                )}
                <div className="btn-group btn-group-sm ml-auto">
                    <FormattedMessage {...(expanded ? messages.rte.toolbar.collapse : messages.rte.toolbar.expand)}>
                        {(title) => (
                            <ToolbarButton onClick={this.toggleExpanded} title={title}>
                                <FontAwesomeIcon icon={expanded ? 'compress' : 'expand'} fixedWidth={true} />
                            </ToolbarButton>
                        )}
                    </FormattedMessage>
                </div>
            </div>
        );
        const editorField = (
            <Editor
                blockRendererFn={mediaBlockRenderer}
                blockStyleFn={editorBlockStyleFn}
                editorState={editorState}
                onChange={this.handleChange}
                handleKeyCommand={this.handleKeyCommand}
            />
        );
        return (
            <div className={classNames('rich-editor', {'rich-editor-expanded': expanded}, className)} {...rest}>
                {toolbar}
                {/* Draft.js's EditorState carries a `hasFocus` flag as part of its shared selection.
                    Mounting a second live <Editor> bound to that same editorState (e.g. while the
                    expanded modal below is open) would make both instances fight to sync the DOM
                    selection onto themselves whenever that flag flips, causing the caret to jump
                    unpredictably - most noticeably right next to an atomic (image) block, where
                    selection handling is already delicate. So only one <Editor> is ever mounted. */}
                {!expanded && editorField}
                {hasImage && <MediaGalleryModal
                    isOpen={this.state.mediaModalOpen}
                    toggle={this.toggleMediaModal}
                    onSelect={this.handleImageSelect}
                />}
                {expanded && (
                    <Modal isOpen={true} toggle={this.toggleExpanded} size="xl" className="rte-expand-modal">
                        <ModalHeader toggle={this.toggleExpanded}>
                            <FormattedMessage {...messages.rte.modals.expand.title} />
                        </ModalHeader>
                        <ModalBody>
                            <div className={classNames('rich-editor', className)}>
                                {toolbar}
                                {editorField}
                            </div>
                        </ModalBody>
                    </Modal>
                )}
            </div>
        );
    }
}

RichEditor.defaultProps = {
    showParameters: true,
    showExtraFields: true,
    showQuestions: false,
    questions: [],
};

export default RichEditor;
